import moment from 'moment';
import {
  activeCommissionsByUserSide,
  asMoney,
  CommissionSide,
  hasCompleteCommissions,
  ICommissionEntry,
  ISheetColumn,
  sideSourceString,
} from './commissionSheet';
import { moneyFormatter } from './helper';

// kan-98 part 6: the executive's second external sheet. Same page, same
// direct-hire rows and same coverage gate as the commission sheet, but a
// payment-first layout — so the T/U source cells and the coverage rule are
// imported from commissionSheet.ts rather than restated here.

/** InvoiceAttachments rows the server already narrowed to check + wired. */
export interface IInvoicePayment {
  id?: string;
  attachmentType?: string | null;
  /** varchar — can arrive as "$1,500.00" from an older hand-typed row. */
  amount?: string | null;
  /** varchar — written by an <input type="date">, so ISO in practice. */
  receivedDate?: string | null;
}

export interface IBuildCompanyActivityRowArgs {
  /** GET_PLACEMENT_BY_ID shape (companyName, invoiceNumber, netCommission...). */
  placement: any;
  /** PLACEMENT_SPLITTING shape: { splitType, splitPercentage, user }. */
  placementSplitting: Array<any>;
  /** dhPlacementCommissions rows for this placement. */
  commissions: Array<any>;
  /** InvoiceAttachments rows for this placement. */
  payments: Array<IInvoicePayment>;
}

export interface ICompanyActivityRow {
  columns: Array<ISheetColumn>;
  /** Every split user on both sides has an active commission entry. */
  commissionsComplete: boolean;
  /** At least one check/wired attachment is on file. */
  hasPayment: boolean;
  /** At least one invoice attachment is on file (a bill sent, not money in). */
  hasInvoice: boolean;
}

const PAYMENT_TYPES = ['check', 'wired'];

const attachmentType = (payment?: IInvoicePayment): string =>
  String(payment?.attachmentType || '').toLowerCase();

const isPayment = (payment?: IInvoicePayment): boolean =>
  PAYMENT_TYPES.includes(attachmentType(payment));

const isInvoice = (payment?: IInvoicePayment): boolean =>
  attachmentType(payment) === 'invoice';

// The date is a varchar, so nothing guarantees the format. ISO first (what the
// modal's <input type="date"> writes), then the sheet's own MM/DD/YYYY, then a
// last native attempt; anything else is ignored rather than becoming today.
const parsePaymentDate = (raw?: string | null): moment.Moment | null => {
  const value = String(raw || '').trim();
  if (!value) return null;

  const iso = moment(value, 'YYYY-MM-DD', true);
  if (iso.isValid()) return iso;

  const us = moment(value, 'MM/DD/YYYY', true);
  if (us.isValid()) return us;

  const native = moment(new Date(value));
  return native.isValid() ? native : null;
};

// Amounts are stored stripped of "$" and "," but only since the modal started
// doing it, so strip again here before trusting Number().
const parsePaymentAmount = (raw?: string | null): number | null => {
  const cleaned = String(raw ?? '').replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : null;
};

const isEntryActive = (entry?: ICommissionEntry): boolean =>
  entry?.status !== 'VOID' &&
  (entry?.entryType === 'COMMISSION' || entry?.entryType === 'CLAWBACK');

// The percentage the side gives away. Flat-rate entries have no rate, so they
// contribute nothing to the % column (their dollars land in Rec/AM Amt).
const sideRatePercent = (
  commissions: Array<any>,
  side: CommissionSide
): number => {
  const given = (commissions || [])
    .filter(
      (entry: ICommissionEntry) =>
        entry?.commissionSide === side &&
        entry?.entryType === 'COMMISSION' &&
        entry?.status !== 'VOID' &&
        entry?.rateType === 'PERCENTAGE'
    )
    .reduce(
      (total: number, entry: ICommissionEntry) =>
        total + (Number(entry?.commissionRate) || 0),
      0
    );

  return Math.round((100 - given) * 1e6) / 1e6;
};

// Clawbacks are stored negative, so a plain sum already nets them out.
const sideCommissionDollars = (
  commissions: Array<any>,
  side: CommissionSide
): number =>
  (commissions || [])
    .filter(
      (entry: ICommissionEntry) =>
        entry?.commissionSide === side && isEntryActive(entry)
    )
    .reduce(
      (total: number, entry: ICommissionEntry) =>
        total + (Number(entry?.commissionAmount) || 0),
      0
    );

const money = (value: number | null): Pick<ISheetColumn, 'value' | 'display'> =>
  value === null
    ? { value: '', display: '' }
    : { value: asMoney(value), display: moneyFormatter(Number(asMoney(value))) };

export const buildCompanyActivityRow = ({
  placement,
  placementSplitting,
  commissions,
  payments,
}: IBuildCompanyActivityRowArgs): ICompanyActivityRow => {
  const commissionByUserSide = activeCommissionsByUserSide(commissions);
  const commissionsComplete = hasCompleteCommissions(
    placementSplitting,
    commissionByUserSide
  );

  // Defensive: the server already filters, but the row set gate depends on it.
  const checkPayments = (payments || []).filter(isPayment);
  const hasPayment = !!checkPayments.length;
  const hasInvoice = (payments || []).some(isInvoice);

  // A placement can be paid in instalments — the sheet wants the last one.
  const latestReceived = checkPayments
    .map((payment) => parsePaymentDate(payment?.receivedDate))
    .filter((date): date is moment.Moment => !!date)
    .sort((a, b) => b.valueOf() - a.valueOf())[0];

  const parsedAmounts = checkPayments
    .map((payment) => parsePaymentAmount(payment?.amount))
    .filter((amount): amount is number => amount !== null);

  let checkAmt: Pick<ISheetColumn, 'value' | 'display'>;
  if (parsedAmounts.length) {
    checkAmt = money(parsedAmounts.reduce((total, amount) => total + amount, 0));
  } else if (checkPayments.length === 1) {
    // Nothing numeric to sum, but a single payment's raw text is still better
    // in the executive's hands than an empty cell.
    const raw = String(checkPayments[0]?.amount ?? '').trim();
    checkAmt = { value: raw, display: raw };
  } else {
    checkAmt = { value: '', display: '' };
  }

  const netCommission = placement?.netCommission;
  const hasNet =
    netCommission !== null &&
    netCommission !== undefined &&
    netCommission !== '' &&
    Number.isFinite(Number(netCommission));

  // Each side starts from half the net billing credit.
  const bucket = hasNet ? Number(netCommission) / 2 : null;

  const sideColumns = (side: CommissionSide) => {
    const percent = hasNet ? sideRatePercent(commissions, side) : null;
    const amount =
      bucket === null ? null : bucket - sideCommissionDollars(commissions, side);
    return { percent, amount };
  };

  const rec = sideColumns('RECRUITING');
  const am = sideColumns('AM');

  const eligible =
    rec.amount === null || am.amount === null ? null : rec.amount + am.amount;

  const percentColumn = (
    percent: number | null
  ): Pick<ISheetColumn, 'value' | 'display'> =>
    percent === null
      ? { value: '', display: '' }
      : {
          // His sheet's column is %-formatted, so the pasted value is the
          // decimal fraction (70 -> 0.7) — same convention as the commission
          // sheet's placement fee.
          value: String(Math.round((percent / 100) * 1e6) / 1e6),
          display: `${percent}%`,
        };

  // Seven untouched columns he fills in by hand inside the sheet.
  const blanks: Array<ISheetColumn> = 'MNOPQRS'
    .split('')
    .map((letter) => ({ letter, name: '', value: '' }));

  const columns: Array<ISheetColumn> = [
    {
      letter: 'A',
      name: 'Check Received',
      value: latestReceived ? latestReceived.format('MM/DD/YYYY') : '',
    },
    { letter: 'B', name: 'Invoice Number', value: placement?.invoiceNumber || '' },
    // No -RPO suffix on this sheet, unlike the commission sheet's client cell.
    { letter: 'C', name: 'Client', value: placement?.companyName || '' },
    { letter: 'D', name: 'Name', value: placement?.candidateName || '' },
    { letter: 'E', name: 'Check Amt', ...checkAmt },
    { letter: 'F', name: 'Rec Bucket', ...money(bucket) },
    { letter: 'G', name: 'AM Bucket', ...money(bucket) },
    { letter: 'H', name: 'Rec %', ...percentColumn(rec.percent) },
    { letter: 'I', name: 'Rec Amt', ...money(rec.amount) },
    { letter: 'J', name: 'AM %', ...percentColumn(am.percent) },
    { letter: 'K', name: 'AM Amt', ...money(am.amount) },
    { letter: 'L', name: 'Eligible Amt', ...money(eligible) },
    ...blanks,
    // T before U on purpose: this sheet lists AM ahead of Rec.
    {
      letter: 'T',
      name: 'AM',
      value: sideSourceString(
        'ACCOUNT_MANAGER',
        placementSplitting,
        commissionByUserSide
      ),
    },
    {
      letter: 'U',
      name: 'Rec',
      value: sideSourceString(
        'RECRUITING',
        placementSplitting,
        commissionByUserSide
      ),
    },
  ];

  return { columns, commissionsComplete, hasPayment, hasInvoice };
};
